import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { enqueueOfflineRequest } from '../utils/offlineQueue';
import '../styles/Puantaj.css';
import * as XLSX from 'xlsx';

const Puantaj = () => {
  const { user } = useAuth();

  const [entries, setEntries] = useState([]);
  const [pendingEntries, setPendingEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamEmployees, setTeamEmployees] = useState([]);

  const [filteredEntries, setFilteredEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncNotice, setSyncNotice] = useState('');

  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [expandedTeams, setExpandedTeams] = useState({});
  const [listMode, setListMode] = useState('grouped');

  const isManager = [1, 2, 3].includes(Number(user?.role_id));

  const fetchPuantajEntries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${API_BASE_URL}/puantaj`, {
        validateStatus: () => true,
      });

      if (response.status === 200) {
        setEntries(Array.isArray(response.data) ? response.data : []);
      } else {
        setEntries([]);
      }
    } catch (err) {
      setError('Puantaj verileri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPendingEntries = useCallback(async () => {
    if (!isManager) {
      setPendingEntries([]);
      return;
    }

    try {
      const response = await apiClient.get(`${API_BASE_URL}/puantaj/pending`);
      setPendingEntries(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setPendingEntries([]);
    }
  }, [isManager]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/projects`);
      setProjects(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setProjects([]);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/users/options`);
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      try {
        const fallback = await apiClient.get(`${API_BASE_URL}/users`);
        setUsers(Array.isArray(fallback.data) ? fallback.data : []);
      } catch (innerErr) {
        setUsers(user?.id ? [user] : []);
      }
    }
  }, [user]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/teams`);
      setTeams(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setTeams([]);
    }
  }, []);

  const fetchTeamEmployees = useCallback(async (teamId) => {
    if (!teamId) {
      setTeamEmployees([]);
      return;
    }

    try {
      const response = await apiClient.get(`${API_BASE_URL}/teams/${teamId}/employees`);
      setTeamEmployees(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setTeamEmployees([]);
    }
  }, []);

  useEffect(() => {
    fetchPuantajEntries();
    fetchPendingEntries();
    fetchProjects();
    fetchUsers();
    fetchTeams();
  }, [fetchPuantajEntries, fetchPendingEntries, fetchProjects, fetchUsers, fetchTeams]);

  useEffect(() => {
    let nextEntries = entries;

    if (statusFilter !== 'all') {
      nextEntries = nextEntries.filter((entry) => entry.status === statusFilter);
    }

    if (dateFilter) {
      nextEntries = nextEntries.filter((entry) => {
        const entryDate = new Date(entry.work_date).toISOString().split('T')[0];
        return entryDate === dateFilter;
      });
    }

    setFilteredEntries(nextEntries);
  }, [entries, statusFilter, dateFilter]);

  const teamLinkedUsers = useMemo(() => {
    const normalize = (value) =>
      String(value || '')
        .trim()
        .toLowerCase()
        .replace(/ç/g, 'c')
        .replace(/ğ/g, 'g')
        .replace(/ı/g, 'i')
        .replace(/ö/g, 'o')
        .replace(/ş/g, 's')
        .replace(/ü/g, 'u')
        .replace(/[^a-z0-9]+/g, '');

    const seen = new Set();

    return teamEmployees
      .map((employee) => {
        let resolvedUserId = employee.user_id ? Number(employee.user_id) : null;
        const directUser = resolvedUserId
          ? users.find((item) => Number(item.id) === Number(resolvedUserId))
          : null;

        if (directUser) {
          resolvedUserId = Number(directUser.id);
        }

        if (!resolvedUserId) {
          const matchedUser = users.find(
            (item) =>
              normalize(item.username) === normalize(employee.name) ||
              normalize(item.username) === normalize(employee.user_username) ||
              (employee.email && normalize(item.email) === normalize(employee.email)) ||
              (employee.contact_info && normalize(item.email) === normalize(employee.contact_info)) ||
              (employee.user_email && normalize(item.email) === normalize(employee.user_email))
          );
          resolvedUserId = matchedUser ? Number(matchedUser.id) : null;
        }

        const displayName = employee.user_username || employee.name;
        return {
          id: resolvedUserId,
          label: `${displayName}${employee.position ? ` (${employee.position})` : ''}`,
        };
      })
      .filter((employee) => employee.id)
      .filter((employee) => {
        const id = Number(employee.id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [teamEmployees, users]);

  const selectableUsers = useMemo(() => {
    if (selectedTeamId) {
      if (teamLinkedUsers.length > 0) {
        return teamLinkedUsers;
      }
      if (teamEmployees.length === 0) {
        return users.map((item) => ({ id: Number(item.id), label: `${item.username} (genel liste)` }));
      }
      return [];
    }

    if (users.length > 0) {
      return users.map((item) => ({ id: Number(item.id), label: item.username }));
    }

    if (user?.id) {
      return [{ id: Number(user.id), label: user.username || `Kullanıcı ${user.id}` }];
    }

    return [];
  }, [selectedTeamId, teamLinkedUsers, teamEmployees, users, user]);

  useEffect(() => {
    if (selectedTeamId && !selectedUserId && selectableUsers.length > 0) {
      setSelectedUserId(String(selectableUsers[0].id));
    }
  }, [selectedTeamId, selectedUserId, selectableUsers]);

  const openCreateForm = () => {
    setEditingEntry(null);
    setSelectedTeamId('');
    setTeamEmployees([]);
    setSelectedUserId(user?.id ? String(user.id) : '');
    setShowForm(true);
    setError(null);
  };

  const startEditing = (entry) => {
    setEditingEntry(entry);
    setSelectedTeamId('');
    setTeamEmployees([]);
    setSelectedUserId(entry?.user_id ? String(entry.user_id) : user?.id ? String(user.id) : '');
    setShowForm(true);
    setError(null);
  };

  const handleTeamChange = async (teamId) => {
    setSelectedTeamId(teamId);
    setSelectedUserId('');
    await fetchTeamEmployees(teamId);
  };

  const handleCreateEntry = async (payload) => {
    try {
      await apiClient.post(`${API_BASE_URL}/puantaj`, payload);
      await fetchPuantajEntries();
      await fetchPendingEntries();
      setShowForm(false);
      setError(null);
      setSyncNotice('');
    } catch (err) {
      if (!err.response) {
        enqueueOfflineRequest({
          method: 'post',
          url: `${API_BASE_URL}/puantaj`,
          data: payload,
          label: 'Puantaj kaydı',
        });
        setShowForm(false);
        setError(null);
        setSyncNotice('Bağlantı olmadığı için puantaj kaydı saha kuyruğuna alındı. Ağ gelince otomatik gönderilecek.');
        return;
      }
      setError('Puantaj girişi oluşturulurken hata oluştu.');
    }
  };

  const handleUpdateEntry = async (id, payload) => {
    try {
      await apiClient.put(`${API_BASE_URL}/puantaj/${id}`, payload);
      await fetchPuantajEntries();
      await fetchPendingEntries();
      setEditingEntry(null);
      setShowForm(false);
      setError(null);
      setSyncNotice('');
    } catch (err) {
      if (!err.response) {
        enqueueOfflineRequest({
          method: 'put',
          url: `${API_BASE_URL}/puantaj/${id}`,
          data: payload,
          label: 'Puantaj güncelleme',
        });
        setEditingEntry(null);
        setShowForm(false);
        setError(null);
        setSyncNotice('Bağlantı olmadığı için puantaj güncellemesi saha kuyruğuna alındı. Ağ gelince otomatik gönderilecek.');
        return;
      }
      setError('Puantaj girişi güncellenirken hata oluştu.');
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!window.confirm('Bu puantaj girişini silmek istediğinize emin misiniz?')) return;

    try {
      await apiClient.delete(`${API_BASE_URL}/puantaj/${id}`);
      await fetchPuantajEntries();
      await fetchPendingEntries();
    } catch (err) {
      setError('Puantaj girişi silinirken hata oluştu.');
    }
  };

  const handleApprove = async (id) => {
    try {
      await apiClient.put(`${API_BASE_URL}/puantaj/${id}`, { status: 'approved' });
      await fetchPuantajEntries();
      await fetchPendingEntries();
    } catch (err) {
      setError('Puantaj girişi onaylanırken hata oluştu.');
    }
  };

  const handleReject = async (id) => {
    try {
      await apiClient.put(`${API_BASE_URL}/puantaj/${id}`, { status: 'rejected' });
      await fetchPuantajEntries();
      await fetchPendingEntries();
    } catch (err) {
      setError('Puantaj girişi reddedilirken hata oluştu.');
    }
  };

  const handleExportExcel = () => {
    const workbook = XLSX.utils.book_new();
    const exportDate = new Date().toISOString().split('T')[0];

    const rawRows = filteredEntries.map((entry) => ({
      Calisan: entry.user_username || `Kullanıcı ${entry.user_id}`,
      Ekip: entry.team_name || '-',
      Proje: entry.project_name || '-',
      Unvan: entry.task_title || entry.user_job_title || '-',
      Tarih: new Date(entry.work_date).toLocaleDateString('tr-TR'),
      Yil_Ay: new Date(entry.work_date).toISOString().slice(0, 7),
      Saat: parseFloat(entry.hours_worked) || 0,
      Durum: entry.status || '-',
      Açıklama: entry.description || '-',
    }));

    const perEmployee = rawRows.reduce((acc, row) => {
      const key = row.Calisan;
      acc[key] = acc[key] || { Calisan: key, Toplam_Saat: 0, Kayit_Sayisi: 0, Pending: 0, Approved: 0, Rejected: 0 };
      acc[key].Toplam_Saat += row.Saat;
      acc[key].Kayit_Sayisi += 1;
      if (row.Durum === 'pending') acc[key].Pending += 1;
      if (row.Durum === 'approved') acc[key].Approved += 1;
      if (row.Durum === 'rejected') acc[key].Rejected += 1;
      return acc;
    }, {});

    const perProject = rawRows.reduce((acc, row) => {
      const key = row.Proje;
      acc[key] = acc[key] || { Proje: key, Toplam_Saat: 0, Kayit_Sayisi: 0 };
      acc[key].Toplam_Saat += row.Saat;
      acc[key].Kayit_Sayisi += 1;
      return acc;
    }, {});

    const perTeam = rawRows.reduce((acc, row) => {
      const key = row.Ekip || '-';
      acc[key] = acc[key] || { Ekip: key, Toplam_Saat: 0, Kayit_Sayisi: 0, Kisi_Sayisi: 0 };
      acc[key].Toplam_Saat += row.Saat;
      acc[key].Kayit_Sayisi += 1;
      return acc;
    }, {});

    Object.keys(perTeam).forEach((teamName) => {
      const uniqueUsers = new Set(rawRows.filter((row) => row.Ekip === teamName).map((row) => row.Calisan));
      perTeam[teamName].Kisi_Sayisi = uniqueUsers.size;
    });

    const makeSheet = (rows, cols) => {
      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet['!cols'] = cols.map((width) => ({ wch: width }));
      worksheet['!autofilter'] = { ref: worksheet['!ref'] };
      return worksheet;
    };

    XLSX.utils.book_append_sheet(workbook, makeSheet(rawRows, [22, 20, 22, 24, 14, 10, 10, 12, 42]), 'Puantaj_Kayitlari');
    XLSX.utils.book_append_sheet(
      workbook,
      makeSheet(Object.values(perEmployee).map((row) => ({ ...row, Toplam_Saat: Number(row.Toplam_Saat.toFixed(2)) })), [22, 14, 14, 10, 10, 10]),
      'Kisi_Ozet'
    );
    XLSX.utils.book_append_sheet(
      workbook,
      makeSheet(Object.values(perTeam).map((row) => ({ ...row, Toplam_Saat: Number(row.Toplam_Saat.toFixed(2)) })), [24, 14, 14, 12]),
      'Ekip_Ozet'
    );
    XLSX.utils.book_append_sheet(
      workbook,
      makeSheet(Object.values(perProject).map((row) => ({ ...row, Toplam_Saat: Number(row.Toplam_Saat.toFixed(2)) })), [24, 14, 14]),
      'Proje_Ozet'
    );

    XLSX.writeFile(workbook, `Puantaj_Raporu_${exportDate}.xlsx`);
  };

  const getStatusColor = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'pending':
        return 'status-pending';
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  const getStatusLabel = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'pending':
        return 'Beklemede';
      case 'approved':
        return 'Onaylandı';
      case 'rejected':
        return 'Reddedildi';
      default:
        return 'Beklemede';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  const totalHours = filteredEntries.reduce((sum, entry) => sum + (parseFloat(entry.hours_worked) || 0), 0);
  const pendingCount = filteredEntries.filter((entry) => entry.status === 'pending').length;
  const approvedCount = filteredEntries.filter((entry) => entry.status === 'approved').length;

  const perUserTotals = filteredEntries.reduce((acc, entry) => {
    const name = entry.user_username || `Kullanıcı ${entry.user_id}`;
    acc[name] = (acc[name] || 0) + (parseFloat(entry.hours_worked) || 0);
    return acc;
  }, {});

  const mobileTeamGroups = useMemo(() => {
    const grouped = filteredEntries.reduce((acc, entry) => {
      const teamName = String(entry.team_name || 'Ekipsiz Kayıtlar');
      if (!acc[teamName]) {
        acc[teamName] = { teamName, totalHours: 0, entries: [] };
      }
      acc[teamName].entries.push(entry);
      acc[teamName].totalHours += parseFloat(entry.hours_worked) || 0;
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => b.totalHours - a.totalHours);
  }, [filteredEntries]);

  const toggleMobileTeamGroup = (teamName) => {
    setExpandedTeams((prev) => ({ ...prev, [teamName]: !prev[teamName] }));
  };

  if (loading) {
    return <div className="puantaj-container loading">Yükleniyor...</div>;
  }

  return (
    <div className="puantaj-container">
      <div className="puantaj-header">
        <h1>Puantaj Yönetimi</h1>
        <div className="header-stats">
          <div className="stat-box">
            <div className="stat-label">Toplam Saat</div>
            <div className="stat-value">{totalHours.toFixed(1)}</div>
          </div>
          <div className="stat-box">
            <div className="stat-label">Beklemede</div>
            <div className="stat-value">{pendingCount}</div>
          </div>
          <div className="stat-box">
            <div className="stat-label">Onaylandı</div>
            <div className="stat-value">{approvedCount}</div>
          </div>
        </div>
      </div>

      {error && <div className="error-alert">{error}</div>}
      {syncNotice && <div className="success-alert">{syncNotice}</div>}

      <div className="puantaj-controls">
        <button className="btn-new-entry" onClick={openCreateForm} type="button">
          Yeni Puantaj Gir
        </button>
        <button className="btn-new-entry btn-export" onClick={handleExportExcel} type="button">
          Excel'e Aktar
        </button>

        <div className="filter-group">
          <button className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`} onClick={() => setStatusFilter('all')} type="button">
            Tümü ({entries.length})
          </button>
          <button className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`} onClick={() => setStatusFilter('pending')} type="button">
            Beklemede ({pendingCount})
          </button>
          <button className={`filter-btn ${statusFilter === 'approved' ? 'active' : ''}`} onClick={() => setStatusFilter('approved')} type="button">
            Onaylandı ({approvedCount})
          </button>
        </div>

        <div className="filter-group">
          <button className={`filter-btn ${listMode === 'grouped' ? 'active' : ''}`} onClick={() => setListMode('grouped')} type="button">
            Ekip Görünümü
          </button>
          <button className={`filter-btn ${listMode === 'table' ? 'active' : ''}`} onClick={() => setListMode('table')} type="button">
            Tablo
          </button>
        </div>

        <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="date-filter-input" />
      </div>

      {isManager && (
        <div className="pending-review-panel">
          <div className="pending-review-header">
            <strong>Onay Bekleyen Puantajlar ({pendingEntries.length})</strong>
            <button className="btn-small btn-edit" onClick={fetchPendingEntries} type="button">
              Yenile
            </button>
          </div>
          {pendingEntries.length === 0 ? (
            <div className="pending-review-empty">Onay bekleyen kayıt yok.</div>
          ) : (
            <div className="pending-review-list">
              {pendingEntries.slice(0, 8).map((entry) => (
                <div key={`pending-${entry.id}`} className="pending-review-card">
                  <div className="pending-review-info">
                    <strong>{entry.user_username || `Kullanıcı ${entry.user_id}`}</strong>
                    {entry.project_name ? ` - ${entry.project_name}` : ''}
                    {' '}({parseFloat(entry.hours_worked).toFixed(1)}h)
                  </div>
                  <div className="pending-review-actions">
                    <button className="btn-small btn-approve" onClick={() => handleApprove(entry.id)} type="button">
                      Onayla
                    </button>
                    <button className="btn-small btn-reject" onClick={() => handleReject(entry.id)} type="button">
                      Reddet
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{editingEntry ? 'Puantaj Düzenle' : 'Yeni Puantaj Gir'}</h2>
              <button className="btn-close" onClick={() => setShowForm(false)} type="button">
                ×
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                const submission = new FormData(e.target);
                const data = Object.fromEntries(submission);

                if (!editingEntry && teams.length > 0 && !selectedTeamId) {
                  setError('Lütfen önce ekip seçin.');
                  return;
                }

                const resolvedUserId = parseInt(selectedUserId || data.user_id || user?.id, 10);
                if (!resolvedUserId) {
                  setError('Lütfen bir çalışan seçin.');
                  return;
                }

                data.work_date = data.entry_date;
                delete data.entry_date;
                data.project_id = parseInt(data.project_id, 10);
                data.user_id = resolvedUserId;
                data.description = data.description || '';

                if (editingEntry) {
                  handleUpdateEntry(editingEntry.id, data);
                } else {
                  handleCreateEntry(data);
                }
              }}
            >
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Ekip</label>
                  <select className="form-input" value={selectedTeamId} onChange={(e) => handleTeamChange(e.target.value)}>
                    <option value="">Ekip Seçiniz</option>
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Çalışan *</label>
                  <select
                    name="user_id"
                    className="form-input"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    required
                  >
                    <option value="">Çalışan Seçiniz</option>
                    {selectableUsers.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                  {selectedTeamId && teamEmployees.length > 0 && teamLinkedUsers.length === 0 && (
                    <small className="field-help field-help-warning">
                      Bu ekipte personel var ancak kullanıcı hesabı eşleşmesi bulunamadı. Ekip kayıtlarını kontrol edin.
                    </small>
                  )}
                  {selectedTeamId && teamEmployees.length === 0 && teamLinkedUsers.length === 0 && (
                    <small className="field-help field-help-warning">
                      Bu ekipte henüz personel bulunmuyor. Genel listeden seçim yapabilirsiniz.
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Proje *</label>
                  <select name="project_id" className="form-input" defaultValue={editingEntry?.project_id || ''} required>
                    <option value="">Proje Seçiniz</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Tarih *</label>
                  <input
                    type="date"
                    name="entry_date"
                    className="form-input"
                    defaultValue={editingEntry?.work_date?.split('T')[0] || new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Çalışılan Saat *</label>
                  <input
                    type="number"
                    name="hours_worked"
                    className="form-input"
                    step="0.5"
                    min="0"
                    max="24"
                    defaultValue={editingEntry?.hours_worked || ''}
                    required
                  />
                </div>
              </div>

              <div className="form-group full">
                <label className="form-label">Notlar</label>
                <textarea name="description" className="form-textarea" defaultValue={editingEntry?.description || ''} />
              </div>

              {!editingEntry && isManager && (
                <div className="form-group">
                  <label className="form-label">Durum</label>
                  <select name="status" className="form-select" defaultValue="pending">
                    <option value="pending">Beklemede</option>
                    <option value="approved">Onaylandı</option>
                    <option value="rejected">Reddedildi</option>
                  </select>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  {editingEntry ? 'Güncelle' : 'Kaydet'}
                </button>
                <button type="button" className="btn-cancel" onClick={() => setShowForm(false)}>
                  İptal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {filteredEntries.length > 0 && listMode === 'grouped' && (
        <div className="puantaj-mobile-groups">
          {mobileTeamGroups.map((group) => {
            const isOpen = Boolean(expandedTeams[group.teamName]);
            return (
              <section key={`team-group-${group.teamName}`} className={`mobile-team-group ${isOpen ? 'open' : ''}`}>
                <button type="button" className="mobile-team-group-toggle" onClick={() => toggleMobileTeamGroup(group.teamName)}>
                  <div className="mobile-team-group-meta">
                    <strong>{group.teamName}</strong>
                    <span>{group.entries.length} kayıt</span>
                  </div>
                  <div className="mobile-team-group-hours">{group.totalHours.toFixed(1)}h</div>
                </button>

                {isOpen && (
                  <div className="mobile-team-group-body">
                    {group.entries.map((entry) => (
                      <div key={`mobile-entry-${entry.id}`} className="mobile-entry-card">
                        <div className="mobile-entry-top">
                          <strong>{entry.user_username || `Kullanıcı ${entry.user_id}`}</strong>
                          <span className={`status-badge ${getStatusColor(entry.status)}`}>{getStatusLabel(entry.status)}</span>
                        </div>
                        <div className="mobile-entry-row">
                          <span>{formatDate(entry.work_date)}</span>
                          <strong>{parseFloat(entry.hours_worked).toFixed(1)}h</strong>
                        </div>
                        {entry.description && <div className="mobile-entry-note">{entry.description}</div>}
                        <div className="mobile-entry-actions">
                          {isManager && entry.status === 'pending' && (
                            <>
                              <button className="btn-small btn-approve" onClick={() => handleApprove(entry.id)} type="button">
                                Onayla
                              </button>
                              <button className="btn-small btn-reject" onClick={() => handleReject(entry.id)} type="button">
                                Reddet
                              </button>
                            </>
                          )}
                          <button className="btn-small btn-edit" onClick={() => startEditing(entry)} type="button">
                            Düzenle
                          </button>
                          <button className="btn-small btn-delete" onClick={() => handleDeleteEntry(entry.id)} type="button">
                            Sil
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {filteredEntries.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">-</div>
          <div className="empty-text">Puantaj girişi bulunamadı</div>
          <button className="empty-action" onClick={openCreateForm} type="button">
            İlk Puantaj Girişini Oluştur
          </button>
        </div>
      ) : listMode === 'table' ? (
        <div className="puantaj-table">
          <div className="table-header">
            <div>İşçi Adı</div>
            <div>Tarih</div>
            <div>Saat</div>
            <div>Açıklama</div>
            <div>Durum</div>
            <div>Onay</div>
            <div>İşlem</div>
          </div>
          <div className="table-body">
            {filteredEntries.map((entry) => (
              <div key={entry.id} className="table-row">
                <div className="table-cell" data-label="İşçi">
                  <span className="worker-name">{entry.user_username || `Kullanıcı ${entry.user_id}`}</span>
                </div>
                <div className="table-cell" data-label="Tarih">
                  <span className="date-cell">{formatDate(entry.work_date)}</span>
                </div>
                <div className="table-cell" data-label="Saat">
                  <span className="hours-cell">{parseFloat(entry.hours_worked).toFixed(1)}h</span>
                </div>
                <div className="table-cell" data-label="Açıklama">
                  <span className="entry-description">{entry.description || '-'}</span>
                </div>
                <div className="table-cell" data-label="Durum">
                  <span className={`status-badge ${getStatusColor(entry.status)}`}>{getStatusLabel(entry.status)}</span>
                </div>
                <div className="table-cell" data-label="Onay">
                  <div className="table-actions">
                    {isManager && entry.status === 'pending' && (
                      <>
                        <button className="btn-small btn-approve" onClick={() => handleApprove(entry.id)} type="button" title="Onayla">
                          Onayla
                        </button>
                        <button className="btn-small btn-reject" onClick={() => handleReject(entry.id)} type="button" title="Reddet">
                          Reddet
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="table-cell" data-label="İşlem">
                  <div className="table-actions">
                    <button className="btn-small btn-edit" onClick={() => startEditing(entry)} type="button" title="Düzenle">
                      Düzenle
                    </button>
                    <button className="btn-small btn-delete" onClick={() => handleDeleteEntry(entry.id)} type="button" title="Sil">
                      Sil
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="puantaj-summary-footer">
            <strong>Toplam Saat: </strong> {totalHours.toFixed(1)}h
            <div className="puantaj-summary-grid">
              {Object.keys(perUserTotals).map((name) => (
                <div key={name} className="puantaj-summary-pill">
                  <div className="puantaj-summary-user">{name}</div>
                  <div className="puantaj-summary-hours">{perUserTotals[name].toFixed(1)}h</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Puantaj;
